import React from 'react';
import { ArrowUpRight, Bot, Clock, Database, Mail, MessageCircle, Wallet } from 'lucide-react';
import { Button } from './ui/button';

const VALUE_DELIVERED = {
  manhoursSavedYTD: 6240,
  manhoursSavedYoY: 42,
  costSavedYTD: 4820000,
  subscriptionCost: 1100000,
  agentsRunning: 9,
  dataSourcesReconciled: 14,
};

const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
}).format(value);

const getInitials = (name) => (name || 'Business Planner')
  .split(' ')
  .filter(Boolean)
  .map((part) => part[0])
  .slice(0, 2)
  .join('')
  .toUpperCase();

function ValueTile({ Icon, label, value, sub, positive = false }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/60 p-3">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-primary" />
        <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      </div>
      <p className="mt-1.5 font-display text-lg font-semibold leading-none">{value}</p>
      <p className={`mt-1 text-[11px] ${positive ? 'text-success' : 'text-muted-foreground'}`}>{sub}</p>
    </div>
  );
}

export function ValueDeliveredPanel({ orgMember = {} }) {
  const plannerName = orgMember.business_planner_name || orgMember.planner_name || 'Arjun Mehta';
  const plannerRole = orgMember.business_planner_role || orgMember.planner_role || 'Business Planner';
  const plannerEmail = orgMember.business_planner_email || orgMember.planner_email || '';
  const plannerPhone = orgMember.business_planner_phone || orgMember.planner_phone || '';
  const roi = (VALUE_DELIVERED.costSavedYTD / VALUE_DELIVERED.subscriptionCost).toFixed(1);
  const plannerBio = 'Your dedicated planner — leads monthly reviews, board packs and the automation roadmap for your business.';

  return (
    <section className="px-6 lg:px-10 mt-8">
      <div className="surface-card relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ backgroundImage: 'var(--gradient-aurora)' }}
        />
        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-0">
          <div className="lg:col-span-2 p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Value Delivered · YTD</p>
            <h3 className="mt-1 font-display text-xl font-semibold">
              IMAGE-I has saved your team {VALUE_DELIVERED.manhoursSavedYTD.toLocaleString('en-IN')} hours this year.
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {roi}× return on subscription · {VALUE_DELIVERED.agentsRunning} agents running across {VALUE_DELIVERED.dataSourcesReconciled} data sources.
            </p>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <ValueTile Icon={Clock} label="Manhours saved" value={VALUE_DELIVERED.manhoursSavedYTD.toLocaleString('en-IN')} sub={`+${VALUE_DELIVERED.manhoursSavedYoY}% YoY`} positive />
              <ValueTile Icon={Wallet} label="Cost saved" value={formatCurrency(VALUE_DELIVERED.costSavedYTD)} sub={`${roi}× of fee`} positive />
              <ValueTile Icon={Bot} label="Agents running" value={String(VALUE_DELIVERED.agentsRunning)} sub="24/7 monitoring" />
              <ValueTile Icon={Database} label="Sources reconciled" value={String(VALUE_DELIVERED.dataSourcesReconciled)} sub="Daily" />
            </div>
          </div>

          <div className="border-t lg:border-t-0 lg:border-l border-border/60 p-5 flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Your Business Planner</p>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-11 w-11 shrink-0 rounded-full bg-[image:var(--gradient-primary)] grid place-items-center text-primary-foreground font-display font-semibold surface-glow">
                {getInitials(plannerName)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{plannerName}</p>
                <p className="text-[11px] text-muted-foreground">{plannerRole}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{plannerBio}</p>

            {(plannerEmail || plannerPhone) && (
              <div className="mt-3 rounded-md border border-border/60 bg-muted/30 p-2 text-[11px]">
                <p className="text-muted-foreground">Planner contact</p>
                <p className="mt-0.5 font-medium truncate">{plannerEmail || plannerPhone}</p>
              </div>
            )}

            <div className="mt-auto pt-4 flex gap-2">
              <Button size="sm" className="flex-1" asChild={Boolean(plannerEmail)}>
                {plannerEmail ? (
                  <a href={`mailto:${plannerEmail}`}>
                    <Mail className="h-3.5 w-3.5 mr-1.5" /> Message
                  </a>
                ) : (
                  <span><MessageCircle className="h-3.5 w-3.5 mr-1.5" /> Message</span>
                )}
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                Book review <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
